import React from 'react';

import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';

import { useBoolean, useDebounce } from '@looko-corp/acloset-react-kit/src/react';

import { LookoButton } from '../Button/LookoButton';
import { HStack, VStack } from '../Stack';

export interface LookoTutorialModalProps {
  /**
   * 알림을 열거나 닫습니다.
   */
  open: boolean;

  /**
   * 튜토리얼 컨텐츠 리스트
   */
  contents?: LookoTutorialModalContentProps[];

  /**
   * 확인 버튼 텍스트.
   */
  confirmButtonText?: string;

  /**
   * 확인 버튼 텍스트 색상.
   */
  confirmButtonColor?: React.ComponentProps<typeof LookoButton>['color'];

  /**
   * 거절 버튼 텍스트.
   *
   * undefined 일 경우, 버튼을 그리지 않습니다.
   */
  rejectButtonText?: string;

  /**
   * 거절 버튼 텍스트 색상.
   */
  rejectButtonColor?: React.ComponentProps<typeof LookoButton>['color'];

  /**
   * 취소 버튼 텍스트.
   */
  cancelButtonText?: string;

  /**
   * escape 버튼 또는 백드롭 클릭으로 인한 알림 닫기 요청을 비활성화 합니다.
   */
  disableEscape?: boolean;

  /**
   * 확인 버튼 이벤트.
   *
   * True 시에 알림을 닫습니다.
   */
  onConfirm?: () => boolean | Promise<boolean>;

  /**
   * 거절 버튼 이벤트.
   *
   * True 시에 알림을 닫습니다.
   */
  onReject?: () => boolean | Promise<boolean>;

  /**
   * 취소 버튼 이벤트.
   */
  onCancel?: () => void;

  /**
   * 알림 닫기를 요청할 때 불립니다.
   */
  onClose?: () => void;
}

export interface LookoTutorialModalContentProps {
  /**
   * 튜토리얼 제목.
   */
  title?: React.ReactNode;

  /**
   * 튜토리얼 내용.
   */
  subText?: React.ReactNode;

  /**
   * 튜토리얼 이미지.
   */
  imageUrl?: string;

  /**
   * 튜토리얼 비디오 URL.
   */
  videoUrl?: string;
}

/**
 * 튜토리얼 모달 기본 형태. 현재 단계 제목을 dialog의 접근 가능한 이름으로 연결하며, 제목이 없으면 스크린 리더용 이름 `안내`를 제공합니다.
 *
 * @param props 모달의 열림 상태, 내용, 사용자 동작을 받아 표시와 콜백을 처리합니다.
 * @returns 현재 튜토리얼 단계와 조작 버튼을 담은 dialog를 반환합니다.
 *
 * @see https://www.figma.com/file/PFrAvDaThtf8qMSRc9pNaV/Looko---Design-System?type=design&node-id=769-51373&mode=design&t=n8dWZCvcx0B2EPnw-0
 */
export const LookoTutorialModal = (props: LookoTutorialModalProps) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const currentContent = props.contents?.[currentIndex];
  const titleId = React.useId();
  const currentTitle = currentContent?.title;
  const hasTitle =
    currentTitle != null &&
    currentTitle !== false &&
    currentTitle !== true &&
    (typeof currentTitle !== 'string' || currentTitle.trim().length > 0);

  const isMulti = (props.contents?.length ?? 0) > 1;

  const { value: isHandling, setValue: setHandling } = useBoolean(false);

  const handleCallback = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onConfirm ? await props.onConfirm() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  const onCancel = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onCancel ? props.onCancel() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  const onReject = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onReject ? await props.onReject() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  React.useLayoutEffect(() => {
    if (!props.open) {
      setHandling(false);
    }

    return () => setCurrentIndex(0);
  }, [props.open, setHandling]);

  return (
    <Dialog
      open={props.open}
      onClose={props.disableEscape ? undefined : props.onClose}
      maxWidth={'md'}
      aria-labelledby={titleId}
    >
      <DialogContent sx={{ px: 4, pt: 6, pb: 3 }}>
        {!hasTitle && (
          <Typography
            id={titleId}
            component="span"
            sx={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            안내
          </Typography>
        )}
        <VStack gap={3} width={416}>
          <VStack gap={2} alignItems={'center'}>
            {hasTitle &&
              (typeof currentTitle === 'string' ? (
                <Typography id={titleId} variant="H2_Looko" style={{ whiteSpace: 'pre-line' }} textAlign={'center'}>
                  {currentTitle}
                </Typography>
              ) : (
                <Box id={titleId}>{currentTitle}</Box>
              ))}

            {currentContent?.subText != null &&
              (typeof currentContent?.subText === 'string' ? (
                <Typography variant="Body_Looko" style={{ whiteSpace: 'pre-line' }} textAlign={'center'}>
                  {currentContent?.subText}
                </Typography>
              ) : (
                currentContent?.subText
              ))}
          </VStack>

          {currentContent?.imageUrl != null && <img src={currentContent?.imageUrl} />}
          {currentContent?.videoUrl != null && (
            <iframe
              className="video"
              title="Youtube player"
              height={262}
              sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-presentation"
              src={currentContent.videoUrl}
              allow="autoplay"
            ></iframe>
          )}
          {isMulti ? (
            <HStack justifyContent={currentIndex === 0 ? 'flex-end' : 'space-between'}>
              {currentIndex !== 0 && (
                <LookoButton
                  startIcon={<KeyboardArrowLeftOutlined />}
                  variant="outlined"
                  onClick={() => setCurrentIndex(prev => prev - 1)}
                >
                  이전
                </LookoButton>
              )}
              {currentIndex === (props.contents?.length ?? 0) - 1 ? (
                <HStack justifyContent={'flex-end'} alignItems={'center'} gap={1}>
                  <LookoButton
                    loading={isHandling}
                    onClick={handleCallback}
                    autoFocus
                    color={props.confirmButtonColor}
                    data-testid="모달 확인 버튼"
                  >
                    {props.confirmButtonText ?? '확인'}
                  </LookoButton>

                  {props.cancelButtonText != null && (
                    <LookoButton onClick={onCancel} variant="outlined">
                      {props.cancelButtonText}
                    </LookoButton>
                  )}
                </HStack>
              ) : (
                <LookoButton
                  endIcon={<KeyboardArrowRightOutlined />}
                  variant="outlined"
                  onClick={() => setCurrentIndex(prev => prev + 1)}
                  data-testid="모달 다음 버튼"
                >
                  다음
                </LookoButton>
              )}
            </HStack>
          ) : (
            <HStack justifyContent={'flex-end'} alignItems={'center'} gap={1}>
              <LookoButton
                loading={isHandling}
                onClick={handleCallback}
                autoFocus
                color={props.confirmButtonColor}
                data-testid="모달 확인 버튼"
              >
                {props.confirmButtonText ?? '확인'}
              </LookoButton>

              {props.rejectButtonText != null && (
                <LookoButton
                  variant="outlined"
                  onClick={onReject}
                  autoFocus
                  color={props.rejectButtonColor}
                  data-testid="모달 거절 버튼"
                >
                  {props.rejectButtonText}
                </LookoButton>
              )}

              {props.cancelButtonText != null && (
                <LookoButton onClick={onCancel} variant="outlined" data-testid="모달 취소 버튼">
                  {props.cancelButtonText}
                </LookoButton>
              )}
            </HStack>
          )}
        </VStack>
      </DialogContent>
    </Dialog>
  );
};
